import React, { useState } from 'react';
import { Result, Button, Space, Typography, Card, List, Divider, Row, Col, theme, Alert } from 'antd';
import { useIntl } from 'react-intl';
import { ExclamationCircleFilled } from '@ant-design/icons';
import BookingStateHook from '@/hooks/useBookingState';
import { CartItem } from '@/AppRedux/actions/booking/bookingType';
import { useSelector } from 'react-redux';
import { useAppSelector } from '@/AppRedux/store/hooks';
import { openPaymentWindow } from '@/utils/paymentWindow';

const Confirmation:React.FC = () => {
  const { bookingRef, cart, discountAmount } = BookingStateHook();
  const intl = useIntl();
  const { token } = theme.useToken();

  const total = cart.reduce((sum, i) => sum + i.subtotal, 0);
  const grand = Math.max(0, total - (discountAmount || 0));

  const paymentData = useSelector((state: any) => state.paymentBeam);
  const { createdLink } = paymentData;

  const confirmPaymentLink = useAppSelector((s) => s.hotelProfile?.hotelSettings?.create_payment_link ?? false);

  const [popupBlocked, setPopupBlocked] = useState(false);

  const onPaymentOption = () => {
    if (!createdLink?.url) return;

    const opened = openPaymentWindow(createdLink.url);
    setPopupBlocked(!opened);
  }

  // ฟังก์ชัน Render Item ให้สวยงามเหมือนหน้า GuestInfo
  const renderConfirmItem = (it: CartItem) => (
    <List.Item style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ width: '100%' }}>
        <Row wrap={false} gutter={16} align="top">
          {/* ส่วนข้อมูลห้อง (ซ้าย) */}
          <Col flex="auto">
            <Typography.Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 4 }}>
              {it.room.roomtype_name} <span style={{ fontWeight: 'normal', color: token.colorTextSecondary }}>× {it.qty}</span>
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: 2 }}>
              {it.rate.rateplan_name}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
              {it.nights} {intl.formatMessage({ id: 'booking.nights' })}
            </Typography.Text>
          </Col>

          {/* ส่วนราคา (ขวา) */}
          <Col flex="none" style={{ textAlign: 'right' }}>
            <Typography.Text strong style={{ fontSize: '16px', color: token.colorPrimary }}>
              ฿ {it.subtotal.toLocaleString()}
            </Typography.Text>
          </Col>
        </Row>
      </div>
    </List.Item>
  );

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', paddingBottom: 40 }}> {/* จัดกึ่งกลางหน้าจอ */}
      <Space orientation="vertical" size={24} style={{ width: '100%' }}>

        {/* ส่วนรายละเอียดรายการจอง */}
        <Card title={intl.formatMessage({ id: 'booking.details' })} style={{ borderRadius: 16 }}>
          <List
            dataSource={cart}
            split={false}
            renderItem={renderConfirmItem}
          />

          <Divider style={{ margin: '16px 0' }} />

          {/* ส่วนสรุปยอดเงิน (จัดซ้าย-ขวา ให้สวยงาม) */}
          <Space orientation="vertical" style={{ width: '100%' }} size={8}>
            <Row justify="space-between">
              <Typography.Text>{intl.formatMessage({ id: 'booking.total' })}:</Typography.Text>
              <Typography.Text>฿ {total.toLocaleString()}</Typography.Text>
            </Row>

            {discountAmount > 0 && (
              <Row justify="space-between">
                <Typography.Text>{intl.formatMessage({ id: 'booking.discount' })}:</Typography.Text>
                <Typography.Text type="danger">- ฿ {discountAmount.toLocaleString()}</Typography.Text>
              </Row>
            )}

            <Divider style={{ margin: '12px 0' }} />

            <Row justify="space-between" align="middle">
              <Typography.Title level={4} style={{ margin: 0 }}>
                {intl.formatMessage({ id: 'booking.grandTotal' })}:
              </Typography.Title>
              <Typography.Title level={3} style={{ margin: 0, color: token.colorPrimary }}>
                ฿ {grand.toLocaleString()}
              </Typography.Title>
            </Row>
          </Space>

        </Card>

        {/* ส่วนแสดงผลลัพธ์ Success */}
        <Card style={{ borderRadius: 16, textAlign: 'center' }}>
          <Result
            status="success"
            title={intl.formatMessage({ id: 'booking.confirmed' })}
            subTitle={
              <div style={{ fontSize: 16, display: bookingRef ? 'block' : 'none' }}>
                {intl.formatMessage({ id: 'booking.ref' })}: <Typography.Text strong copyable style={{ fontSize: 18 }}>{bookingRef ?? '-'}</Typography.Text>
              </div>
            }
            extra={confirmPaymentLink ? [
              <Button
                key="paynow"
                type="primary"
                size="large"
                onClick={onPaymentOption}
                style={{ minWidth: 200, fontWeight: 'bold' }}
              >
                {intl.formatMessage({ id: 'booking.payNow' })}
              </Button>,
            ] : []}
            style={{ paddingBottom: 20 }}
          />
          {confirmPaymentLink ? (
            <Alert
              title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <ExclamationCircleFilled style={{ color: token.colorWarning }} />
                  <span>{intl.formatMessage({ id: 'booking.notComplete' })}</span>
                </div>
              }
              type="warning"
              style={{ marginTop: 16 }}
            />
          ) : (
            <Alert
              title={<Typography.Text strong>{intl.formatMessage({ id: 'payment.success.alert.title' })}</Typography.Text>}
              description={
                <ul style={{ listStyleType: 'disc', paddingLeft: '20px' }}>
                  <li>{intl.formatMessage({ id: 'payment.success.alert.line1' })}</li>
                  <li>{intl.formatMessage({ id: 'payment.success.alert.line2' })}</li>
                  <li>{intl.formatMessage({ id: 'payment.success.alert.line3' })}</li>
                </ul>
              }
              type="info"
              showIcon
              style={{ marginTop: 16, textAlign: 'left' }}
            />
          )}
          {popupBlocked && (
            <Alert
              title={intl.formatMessage({ id: 'booking.paymentPopupBlocked.title' })}
              description={intl.formatMessage({ id: 'booking.paymentPopupBlocked.description' })}
              type="warning"
              showIcon
              style={{ marginTop: 16, textAlign: 'left' }}
            />
          )}
        </Card>


      </Space>
    </div>
  );
}

export default Confirmation